import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import Loader from "../../Component/Loader/Loader";
import InvoiceTemplate from "./invoiceDowloadTemplate";

// Interface for Stats
interface StatItem {
  value: number;
  growth?: number;
}

interface SubscriptionStats {
  activeSubscriptions: StatItem;
  mrr: StatItem;
  renewingSoon: StatItem;
  expiredSubscriptions: StatItem;
  newSubscriptions: StatItem;
  revenue: StatItem;
}

const DATE_FILTERS: [string, string][] = [
  ["thisWeek", "This Week"],
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["thisMonth", "This Month"],
  ["lastMonth", "Last Month"],
  ["allTime", "All Time"],
  ["custom", "Custom"],
];

const CYCLE_LABEL: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  annually: "Yearly",
};

const inr = (n: number | string) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ₹27.4L style for the stats strip
const inrShort = (n: number) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return "₹" + Math.round(n).toLocaleString("en-IN");
};

const cycleLabel = (type: string) => CYCLE_LABEL[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : "—");

const daysTo = (date: string) => date ? moment(date).startOf('day').diff(moment().startOf('day'), 'days') : null;

// Same rules as the tabs in /subscription/subscriptions
const subStatus = (item: any): [string, string] => {
  if (item.invoice_status !== 1) return ["warn", "Unpaid"];
  const d = daysTo(item.invoice_plan_end_date);
  if (d !== null && d < 0) return ["none", "Expired"];
  return ["ok", "Active"];
};

const SubscriptionNew = () => {
  const [dataList, setDataList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);
  const navigate = useNavigate();

  // Stats State
  const [stats, setStats] = useState<SubscriptionStats>({
    activeSubscriptions: { value: 0 },
    mrr: { value: 0 },
    renewingSoon: { value: 0 },
    expiredSubscriptions: { value: 0 },
    newSubscriptions: { value: 0, growth: 0 },
    revenue: { value: 0, growth: 0 },
  });

  // Filters
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("thisMonth");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewFilter, setViewFilter] = useState("All");
  const [planFilter, setPlanFilter] = useState("all");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [plans, setPlans] = useState<string[]>([]);
  const [cycles, setCycles] = useState<string[]>([]);
  const [tabCounts, setTabCounts] = useState({ all: 0, active: 0, renewing: 0, expired: 0, unpaid: 0 });

  // Invoice PDF
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceItem, setInvoiceItem] = useState<any>({});
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Calculate Dates when Dropdown Changes
  useEffect(() => {
    let start = "";
    let end = "";

    switch (dateFilter) {
      case "today":
        start = moment().startOf('day').format('YYYY-MM-DD');
        end = moment().endOf('day').format('YYYY-MM-DD');
        break;
      case "yesterday":
        start = moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD');
        break;
      case "thisWeek":
        start = moment().startOf('week').format('YYYY-MM-DD');
        end = moment().endOf('week').format('YYYY-MM-DD');
        break;
      case "thisMonth":
        start = moment().startOf('month').format('YYYY-MM-DD');
        end = moment().endOf('month').format('YYYY-MM-DD');
        break;
      case "lastMonth":
        start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case "custom":
        // keep the range that was showing; the date inputs change it from here
        return;
      case "allTime":
      default:
        start = "";
        end = "";
    }

    setStartDate(start);
    setEndDate(end);
  }, [dateFilter]);

  // Fetch Subscription List
  const getDataList = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/subscription/subscriptions?page=${page}&limit=${pageSize}&activeTab=${viewFilter}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}&plan=${encodeURIComponent(planFilter)}&cycle=${encodeURIComponent(cycleFilter)}`
      );

      if (response.status === 'success') {
        setDataList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.total);
        if (response.tabCounts) setTabCounts(response.tabCounts);
        if (response.plans) setPlans(response.plans);
        if (response.cycles) setCycles(response.cycles);
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading subscriptions!");
    } finally {
      setIsLoader(false);
    }
  }, [page, pageSize, viewFilter, searchQuery, sortOrder, planFilter, cycleFilter]);

  // Fetch Stats
  const fetchStats = useCallback(async () => {
    if (dateFilter !== "allTime" && (!startDate || !endDate)) return;
    try {
      const response = await getRequest(`/subscription/subscriptionStats?startDate=${startDate}&endDate=${endDate}`);
      if (response.status === 'success' && response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to load subscription stats.");
    }
  }, [startDate, endDate, dateFilter]);

  useEffect(() => {
    getDataList();
  }, [getDataList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const handleDownloadPDF = async (item: any) => {
    setShowInvoice(true);
    setInvoiceItem(item);
    setTimeout(async () => {
      const element = invoiceRef.current;
      if (!element) return;

      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice_${item.inovoice_order_id || Date.now()}.pdf`);
      setShowInvoice(false);
    }, 100);
  };

  // Export the rows currently loaded in the table as CSV
  const exportSubscriptions = () => {
    if (!dataList.length) return toast.warning("Nothing to export");
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Invoice", "Customer ID", "Company", "Bill name", "Plan", "Cycle", "Status", "Amount", "Started", "Next renewal", "Method", "Created"];
    const rows = dataList.map(s => [
      s.inovoice_order_id, s.invoice_user_id?.user_unique_id, s.invoice_user_id?.user_companyName, s.invoice_bill_name,
      s.invoice_plan_name, cycleLabel(s.invoice_plan_type), subStatus(s)[1], Number(s.invoice_amount || 0).toFixed(2),
      s.invoice_plan_start_date ? moment(s.invoice_plan_start_date).format("DD MMM YYYY") : "",
      s.invoice_plan_end_date ? moment(s.invoice_plan_end_date).format("DD MMM YYYY") : "",
      s.invoice_transaction_id?.method || "",
      s.createdAt ? moment(s.createdAt).format("DD MMM YYYY") : "",
    ]);
    const csv = [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `subscriptions-${viewFilter.toLowerCase()}-${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${dataList.length} subscriptions`);
  };

  const changeView = (v: string) => { setViewFilter(v); setPage(1); };

  const resetFilters = () => {
    setSearchText("");
    setSortOrder("newest");
    setPlanFilter("all");
    setCycleFilter("all");
    changeView("All");
  };

  // Switching to Custom from All Time starts from this month
  const handleDateFilterChange = (value: string) => {
    if (value === "custom" && (!startDate || !endDate)) {
      setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
      setEndDate(moment().endOf('month').format('YYYY-MM-DD'));
    }
    setDateFilter(value);
  };

  const displayDateRange = (startDate && endDate)
    ? `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`
    : "All Time";

  const getInitials = (name: string) => {
    const parts = (name || "").trim().split(/\s+/);
    return ((parts[0] || "").charAt(0) + (parts[1] || "").charAt(0)).toUpperCase() || 'S';
  };

  // One stat cell: label, value, icon, growth % or a note
  const StatCell = ({ label, value, icon, growth, note }: { label: string; value: number | string; icon: string; growth?: number; note?: string }) => {
    const hasGrowth = typeof growth === "number";
    return (
      <div className="cx-st-c">
        <div className="cx-st-h"><span>{label}</span><i className={icon}></i></div>
        <div className="cx-st-v">{typeof value === "number" ? value.toLocaleString("en-IN") : value}</div>
        <div className="cx-st-f">
          {hasGrowth ? (
            <>
              <span className={`cx-st-d ${growth === 0 ? "" : growth! >= 0 ? "g" : "b"}`}>
                <i className={growth! >= 0 ? "ri-arrow-right-up-line" : "ri-arrow-right-down-line"}></i>{Math.abs(growth!).toFixed(1)}%
              </span>
              <span>vs prev. period</span>
            </>
          ) : <span>{note}</span>}
        </div>
      </div>
    );
  };

  // Page numbers with ellipsis: 1 … 4 5 6 … 20
  const pageItems = (): (number | string)[] => {
    const out: (number | string)[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== "…") out.push("…");
    }
    return out;
  };

  const chips: [string, string, number][] = [
    ["All", "All", tabCounts.all],
    ["Active", "Active", tabCounts.active],
    ["Renewing", "Renewing in 7d", tabCounts.renewing],
    ["Expired", "Expired", tabCounts.expired],
    ["Unpaid", "Unpaid", tabCounts.unpaid],
  ];

  const firstRow = totalRecords ? (page - 1) * pageSize + 1 : 0;
  const lastRow = totalRecords ? (page - 1) * pageSize + dataList.length : 0;

  const viewCustomer = (item: any) => {
    if (item.invoice_user_id?._id) navigate(`/customers-and-users/view-user/${item.invoice_user_id._id}`);
  };

  return (
    <>
      {(isLoader || showInvoice) && <Loader />}
      <div className="container-fluid">
        <div className="cx">
          {/* --- Page header --- */}
          <div className="cx-hdr">
            <div>
              <ul className="cx-crumb"><li>Master Admin</li><li>Subscriptions</li></ul>
              <h1 className="cx-title">Subscriptions</h1>
              <p className="cx-sub">Every workspace's plan, billing cycle and next renewal.</p>
            </div>
            <div className="cx-actions">
              <div className="cx-pf">
                <select className="cx-select" value={dateFilter} onChange={(e) => handleDateFilterChange(e.target.value)}>
                  {DATE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {dateFilter === "custom" ? (
                  <span className="dr custom">
                    <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} aria-label="From date" />
                    –
                    <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} aria-label="To date" />
                  </span>
                ) : (
                  <span className="dr"><i className="ri-calendar-2-line"></i>{displayDateRange}</span>
                )}
              </div>
              <button type="button" className="cx-btn cx-btn-light" onClick={exportSubscriptions}>
                <i className="ri-download-2-line"></i>Export
              </button>
            </div>
          </div>

          {/* --- Stats strip --- */}
          <div className="cx-st">
            <StatCell label="Active subscriptions" value={stats.activeSubscriptions.value} icon="ri-checkbox-circle-line" note="Right now" />
            <StatCell label="MRR" value={inrShort(stats.mrr.value)} icon="ri-money-rupee-circle-line" note="From active plans" />
            <StatCell label="Renewing in 7 days" value={stats.renewingSoon.value} icon="ri-calendar-event-line" note="Next 7 days" />
            <StatCell label="New subscriptions" value={stats.newSubscriptions.value} icon="ri-add-circle-line" growth={stats.newSubscriptions.growth} />
            <StatCell label="Revenue (period)" value={inrShort(stats.revenue.value)} icon="ri-bank-card-line" growth={stats.revenue.growth} />
          </div>

          {/* --- Table card --- */}
          <div className="cx-card">
            <div className="cx-toolbar">
              <div className="cx-chips">
                {chips.map(([k, l, n]) => (
                  <button type="button" key={k} className={`cx-chip ${viewFilter === k ? "on" : ""}`} onClick={() => changeView(k)}>
                    {l} <b>{n || 0}</b>
                  </button>
                ))}
              </div>
            </div>
            <div className="cx-toolbar">
              <select className="cx-select" value={planFilter} onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}>
                <option value="all">All plans</option>
                {plans.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select className="cx-select" value={cycleFilter} onChange={(e) => { setCycleFilter(e.target.value); setPage(1); }}>
                <option value="all">All cycles</option>
                {cycles.map(c => <option key={c} value={c}>{cycleLabel(c)}</option>)}
              </select>
              <div className="sp"></div>
              <input
                className="cx-input"
                type="search"
                placeholder="Company, email, invoice ID…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <select
                className="cx-select"
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
              >
                <option value="newest">Sort: Newest</option>
                <option value="oldest">Sort: Oldest</option>
                <option value="renewal">Sort: Next renewal</option>
              </select>
              <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
                <i className="ri-refresh-line"></i>Reset
              </button>
            </div>

            <div className="cx-tbl-wrap">
              <table className="cx-tbl">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th className="r">Amount</th>
                    <th>Started</th>
                    <th>Next renewal</th>
                    <th>Method</th>
                    <th className="r">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dataList && dataList.length > 0 ? (
                    dataList.map((item) => {
                      const user = item.invoice_user_id || {};
                      const name = user.user_companyName || item.invoice_bill_name || "—";
                      const [sc, sl] = subStatus(item);
                      const d = daysTo(item.invoice_plan_end_date);
                      return (
                        <tr key={item._id} className="click" onClick={() => viewCustomer(item)}>
                          <td>
                            <div className="cx-who">
                              <div className="av">{getInitials(name)}</div>
                              <div>
                                <b>{name}</b>
                                <small>
                                  {user.user_unique_id && <span className="mono">#{user.user_unique_id}</span>}
                                  {item.inovoice_order_id && <span className="mono"> · {item.inovoice_order_id}</span>}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {item.invoice_plan_name || <span className="muted">—</span>}
                            <div className="fs12 muted">{cycleLabel(item.invoice_plan_type)}</div>
                          </td>
                          <td><span className={`cx-pill ${sc}`}><i></i>{sl}</span></td>
                          <td className="r mono">
                            {Number(item.invoice_amount || 0) ? inr(item.invoice_amount) : <span className="muted">Free</span>}
                          </td>
                          <td className="fs12 muted">
                            {item.invoice_plan_start_date ? moment(item.invoice_plan_start_date).format("DD MMM YYYY") : "—"}
                          </td>
                          <td className="fs12">
                            {item.invoice_plan_end_date && d !== null ? (
                              <>
                                {moment(item.invoice_plan_end_date).format("DD MMM YYYY")}
                                <div
                                  className={d < 0 ? "down" : d <= 7 ? "" : "muted"}
                                  style={{ fontSize: 11, ...(d >= 0 && d <= 7 ? { color: "var(--amber)" } : {}) }}
                                >
                                  {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? "today" : `in ${d}d`}
                                </div>
                              </>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td className="fs12" style={{ textTransform: "uppercase" }}>
                            {item.invoice_transaction_id?.method || <span className="muted">—</span>}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="cx-ra">
                              <button type="button" className="v" title="View customer" onClick={() => viewCustomer(item)}>
                                <i className="ri-eye-line"></i>
                              </button>
                              <button type="button" title="Download invoice" onClick={() => handleDownloadPDF(item)}>
                                <i className="ri-file-download-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8}>
                        <div className="cx-empty">
                          <i className="ri-refresh-line"></i>
                          No subscriptions match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="cx-tfoot">
              <span>Showing <b>{firstRow}–{lastRow}</b> of <b>{(totalRecords || 0).toLocaleString("en-IN")}</b> subscriptions</span>
              <span className="muted">Latest plan invoice per workspace</span>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span>Rows</span>
                <select className="cx-select" value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="cx-pager">
                  <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                  {pageItems().map((p, i) =>
                    typeof p === "number" ? (
                      <button type="button" key={p} className={p === page ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                    ) : (
                      <span key={`gap-${i}`} className="gap">…</span>
                    )
                  )}
                  <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {invoiceItem && showInvoice && <InvoiceTemplate invoiceItem={invoiceItem} invoiceRef={invoiceRef}></InvoiceTemplate>}
    </>
  );
}

export default SubscriptionNew;
